import { isValidEmail } from '@de/utils/binding-enhancer/field-check';
import { formatPhoneNumber } from '@de/utils/formatPhoneNumber';
import { leadSubmitFieldErrorKey, leadSubmitStateKey } from './leadSubmitState';

/** The closed v5 `lead` field set a form input may be tagged with. */
export const LEAD_FIELD_NAMES = [
  'firstName',
  'lastName',
  'email',
  'phoneNumber',
] as const;

export type LeadFieldName = (typeof LEAD_FIELD_NAMES)[number];

export function isLeadFieldName(value: unknown): value is LeadFieldName {
  return (
    typeof value === 'string' &&
    (LEAD_FIELD_NAMES as readonly string[]).includes(value)
  );
}

/** UX checks for one lead field; never stricter than HXLDS. */
export interface LeadFieldPreset {
  /** Error copy for an invalid value, or `null` when it may be sent. */
  readonly validate: (value: string) => string | null;
  /** As-you-type display format. */
  readonly format?: (value: string) => string;
  /** The value sent to the BFF. */
  readonly normalize: (value: string) => string;
  /** Error copy for a blank value on a field marked `required`. */
  readonly requiredMessage: string;
}

type NameCheck = (value: string) => boolean;

const isPresent: NameCheck = (value) => value.trim().length > 0;
const hasNoDigits: NameCheck = (value) => !/\d/.test(value);

function namePreset(
  emptyMessage: string,
  invalidMessage: string,
): LeadFieldPreset {
  const checks: ReadonlyArray<[NameCheck, string]> = [
    [isPresent, emptyMessage],
    [hasNoDigits, invalidMessage],
  ];
  return {
    validate: (value: string): string | null =>
      checks.find(([passes]) => !passes(value))?.[1] ?? null,
    normalize: (value: string): string => value.trim(),
    requiredMessage: emptyMessage,
  };
}

/** Digits only, with the US country code dropped from an 11-digit number. */
function phoneDigits(value: string): string {
  const digits = value.replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
}

export const LEAD_FIELD_PRESETS: Readonly<
  Record<LeadFieldName, LeadFieldPreset>
> = {
  firstName: namePreset('Enter your first name.', 'Enter a valid first name.'),
  lastName: namePreset('Enter your last name.', 'Enter a valid last name.'),
  email: {
    validate: (value) => {
      if (!isPresent(value)) return 'Enter your email.';
      return isValidEmail(value) ? null : 'Enter a valid email.';
    },
    normalize: (value) => value.trim(),
    requiredMessage: 'Enter your email.',
  },
  phoneNumber: {
    validate: (value) => {
      if (!isPresent(value)) return null;
      return phoneDigits(value).length === 10
        ? null
        : 'Enter a valid phone number.';
    },
    format: (value) => {
      const digits = phoneDigits(value);
      // Too many digits stay unformatted so `validate` rejects them.
      return digits.length > 10 ? digits : formatPhoneNumber(digits);
    },
    normalize: phoneDigits,
    requiredMessage: 'Enter your phone number.',
  },
};

export interface LeadFieldInput {
  readonly formKey: string;
  readonly leadField: LeadFieldName;
  readonly value: string;
  /**
   * Pass `BoundComponentInfo.required === true`. Omitted keeps the preset rule:
   * names and email required, phone optional.
   */
  readonly required?: boolean;
}

export interface LeadValidationResult {
  readonly valid: boolean;
  /** Every field error key plus the form error key; `''` clears one. */
  readonly errors: Readonly<Record<string, string>>;
  /** The lead to send: normalized, empty values omitted. Not raw `formState`. */
  readonly lead: Readonly<Partial<Record<LeadFieldName, string>>>;
}

function validateBound(
  preset: LeadFieldPreset,
  value: string,
  required: boolean | undefined,
): string | null {
  if (required === undefined || value.trim() !== '') {
    return preset.validate(value);
  }
  return required ? preset.requiredMessage : null;
}

/**
 * Bound-field errors go to their field keys; the first failing `params.lead`
 * value goes to the form error key. Bound fields win over `params.lead`.
 */
export function validateLeadFields(args: {
  readonly fields: readonly LeadFieldInput[];
  readonly paramsLead?: Readonly<Partial<Record<LeadFieldName, string>>>;
  readonly formName?: string;
}): LeadValidationResult {
  const { fields, paramsLead, formName } = args;
  const errors: Record<string, string> = {};
  const lead: Partial<Record<LeadFieldName, string>> = {};
  const bound = new Set<LeadFieldName>();
  let valid = true;

  for (const { formKey, leadField, value, required } of fields) {
    const key = leadSubmitFieldErrorKey(formKey, formName);
    if (key in errors) continue;
    const preset = LEAD_FIELD_PRESETS[leadField];
    const message = validateBound(preset, value, required);
    errors[key] = message ?? '';
    if (message) valid = false;
    bound.add(leadField);
    const normalized = preset.normalize(value);
    if (normalized && lead[leadField] === undefined) {
      lead[leadField] = normalized;
    }
  }

  let formError = '';
  for (const leadField of LEAD_FIELD_NAMES) {
    const value = paramsLead?.[leadField];
    if (typeof value !== 'string' || value === '' || bound.has(leadField)) {
      continue;
    }
    const preset = LEAD_FIELD_PRESETS[leadField];
    const message = preset.validate(value);
    if (message) {
      valid = false;
      if (!formError) formError = message;
      continue;
    }
    const normalized = preset.normalize(value);
    if (normalized) lead[leadField] = normalized;
  }
  errors[leadSubmitStateKey('error', formName)] = formError;

  return { valid, errors, lead };
}
